import { AnswerType } from "../types/Answer";
import { Dependency } from "../types/Dependency";
import { QuestionType } from "../types/Question";
import { QuestionnaireDetailsType } from "../types/Questionnaire";
import { findElementsByKey } from "../components/organisms/Home/Mountain/landscapeLogic";
import { hasAsweredFunctionsByType } from "../helper/answer"; // adjust import


export function getNextQuestion(
  questions: QuestionType[],
  dependencies: Dependency[],
  answer: AnswerType
): QuestionType {
  const dependency = dependencies.find((d) => d.id === answer.questionId);

  let questionDepId: number;

  if (dependency.answers && dependency.answers.length > 0) {
    const answerDep = dependency.answers.find((a) => a.id === answer.value);
    questionDepId = answerDep?.next_question?.id;
    if (answerDep?.endAfter) {
      return null
    }
  } else {
    if (dependency.endAfter) {
      return null
    }
  }

  if (!questionDepId) questionDepId = dependency?.next_question?.id;

  if (questionDepId) return questions.find((q) => q.id === questionDepId);

  const currentQuestionIndex = questions.findIndex(
    (q) => q.id === answer.questionId
  );

  return questions[currentQuestionIndex + 1];
}

export const isMandatoryQuestionnaire = (keywords: string) => {    
  return !findElementsByKey(keywords);
};


const hasLastQuestionBeenAnswered = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;

  // last by order
  const lastDep = dependencies.reduce<Dependency | null>((acc, d) => {
    if (!acc) return d;
    return (d.order ?? 0) > (acc.order ?? 0) ? d : acc;
  }, null);

  if (!lastDep) return false;

  // ✅ compare by questionId (dependency.id is the question id)
  return sessionAnswers.some(
    (a) =>
      a.questionId === lastDep.id &&
      (hasAsweredFunctionsByType[a.questionType]?.(a.value) ?? false)
  );
};


const hasEndAfterBeenMet = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  for (const ans of sessionAnswers) {
    const dep = dependencies.find((d) => d.id === ans.questionId);
    if (!dep) continue;

    const isAnswered = hasAsweredFunctionsByType[ans.questionType]?.(ans.value) ?? false;
    if (!isAnswered) continue;

    if (dep.endAfter) return true;

    if (dep.answers?.length) {
      const selectedIds = (Array.isArray(ans.value) ? ans.value : [ans.value])
        .map((v) => (typeof v === 'string' ? Number(v) : v))
        .filter((v) => v != null && !Number.isNaN(v as number)) as number[];

      if (dep.answers.some((a) => selectedIds.includes(a.id) && a.endAfter)) {
        return true;
      }
    }
  }
  return false;
};



export const isQuestionnaireDone = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;
  return (
    hasEndAfterBeenMet(dependencies, sessionAnswers) ||
    hasLastQuestionBeenAnswered(dependencies, sessionAnswers)
  );
};

/**
 * Calculate total score of a questionnaire if it is scored
 * @param questions - Questionnaire questions with answers + scores
 * @param sessionAnswers - User's answers
 * @param is_scored - Whether the questionnaire is scored
 */
export function calculateQuestionnaireScore(
  questions: QuestionType[],
  sessionAnswers: AnswerType[],
  is_scored: boolean
): number {
  if (!is_scored) return null;

  let total = 0;

  for (const ans of sessionAnswers) {
    const question = questions.find((q) => q.id === ans.questionId);
    if (!question) continue;

    const coefficient = question.score_coefficient ?? 1;

    // ▼ SELECT-TYPE QUESTIONS ---------------------------------------
    if (["RADIOBUTTON", "CHECKBOX", "DROPDOWN"].includes(question.type)) {
      const detail: any = question.detail;
      if (!detail?.answers) continue;

      // Checkbox returns array, others return single value
      const selectedIds = Array.isArray(ans.value) ? ans.value : [ans.value];

      for (const id of selectedIds) {
        const answerDef = detail.answers.find((a: any) => a.id === id);
        if (answerDef && answerDef.score != null) {
          total += Number(answerDef.score) * coefficient;
        }
      }
    }

    // ▼ SLIDER QUESTIONS --------------------------------------------
    if (question.type === "SLIDER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }

    if (question.type === "NUMBER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }
  }

  return total;
}